import React, { useState, useEffect, useMemo, useCallback } from "react";
import { 
    Card, Table, Row, Col, Form, InputGroup, Button, Pagination, 
    Dropdown, Modal, Alert, Spinner, Badge 
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatNumber, formatDate } from "../utils/formatters";
import './Clients.css';

export default function Clients() {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sortField, setSortField] = useState('raison_sociale');
    const [sortDirection, setSortDirection] = useState('asc');
    const [selectedClients, setSelectedClients] = useState([]);
    const [exportLoading, setExportLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [stats, setStats] = useState(null);

    useEffect(() => {
        fetchClients();
        fetchStats();
    }, []);

    const fetchClients = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/clients.php`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.removeItem("token");
                    localStorage.removeItem("user");
                    navigate("/pages/signin2");
                    throw new Error("Session expirée");
                }
                throw new Error(`Erreur HTTP: ${response.status}`);
            }
            
            const result = await response.json();
            setData(result.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/clients.php?action=statistiques`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur chargement stats", err);
        }
    };

    const fetchClientDetails = async (id) => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/clients.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setSelectedClient(result.data);
                setShowModal(true);
            }
        } catch (err) {
            alert("Erreur lors du chargement des détails");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce client ?")) return;
        
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/clients.php?id=${id}`,
                {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            
            const result = await response.json();
            
            if (result.success) {
                fetchClients();
                fetchStats();
                alert(result.message);
            } else {
                alert(result.message);
            }
        } catch (err) {
            alert("Erreur lors de la suppression");
        }
    };

    const getStatusBadge = (client) => {
        if (client.encours > 0) {
            return { variant: 'warning', text: 'En cours' };
        }
        if (client.total_achats > 0) {
            return { variant: 'success', text: 'Actif' };
        }
        return { variant: 'secondary', text: 'Inactif' };
    };

    const getSoldeClass = (solde) => {
        if (solde > 0) return 'text-danger';
        if (solde < 0) return 'text-success';
        return 'text-secondary';
    };

    // Filtrage et tri
    const filteredAndSortedItems = useMemo(() => {
        let filtered = data.filter(item =>
            item.raison_sociale?.toLowerCase().includes(filterText.toLowerCase()) ||
            item.code_client?.toLowerCase().includes(filterText.toLowerCase()) ||
            item.email?.toLowerCase().includes(filterText.toLowerCase()) ||
            item.telephone?.includes(filterText)
        );

        filtered.sort((a, b) => {
            let aValue = a[sortField];
            let bValue = b[sortField];
            
            if (sortDirection === 'asc') {
                return aValue > bValue ? 1 : -1;
            } else {
                return aValue < bValue ? 1 : -1;
            }
        });

        return filtered;
    }, [filterText, sortField, sortDirection, data]);

    // Pagination
    const totalPages = Math.ceil(filteredAndSortedItems.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentItems = filteredAndSortedItems.slice(startIndex, startIndex + itemsPerPage);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedClients(currentItems.map(c => c.id_client));
        } else {
            setSelectedClients([]);
        }
    };

    const handleSelectClient = (clientId) => {
        setSelectedClients(prev => 
            prev.includes(clientId) 
                ? prev.filter(id => id !== clientId)
                : [...prev, clientId]
        );
    };

    const isAllSelected = currentItems.length > 0 && selectedClients.length === currentItems.length;
    const isSomeSelected = selectedClients.length > 0 && selectedClients.length < currentItems.length;

    // Exports
    const handleExport = (format) => {
        setExportLoading(true);
        const dataToExport = selectedClients.length > 0
            ? data.filter(c => selectedClients.includes(c.id_client))
            : filteredAndSortedItems;

        if (!dataToExport.length) { 
            alert("Aucune donnée à exporter"); 
            setExportLoading(false); 
            return; 
        }

        if (format === "csv") {
            const headers = ["Code","Raison sociale","Téléphone","Email","Total achats","Encours","Plafond","Statut"];
            const csvContent = [
                "\uFEFF" + headers.join(";"),
                ...dataToExport.map(c => [
                    c.code_client,
                    c.raison_sociale,
                    c.telephone || '',
                    c.email || '',
                    c.total_achats || 0,
                    c.encours || 0,
                    c.plafond_credit || 0,
                    getStatusBadge(c).text
                ].map(v => `"${v}"`).join(";"))
            ].join("\n");
            downloadFile(csvContent, "text/csv;charset=utf-8;", `export-clients-${Date.now()}.csv`);

        } else if (format === "excel") {
            const ws = XLSX.utils.json_to_sheet(dataToExport.map(c => ({
                Code: c.code_client,
                "Raison sociale": c.raison_sociale,
                Téléphone: c.telephone,
                Email: c.email,
                "Total achats": c.total_achats,
                Encours: c.encours,
                Plafond: c.plafond_credit,
                Statut: getStatusBadge(c).text
            })));
            
            const columnWidths = [
                { wch: 12 }, { wch: 30 }, { wch: 15 }, { wch: 25 }, 
                { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 12 }
            ];
            ws['!cols'] = columnWidths;
            
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Clients");
            XLSX.writeFile(wb, `export-clients-${Date.now()}.xlsx`);

        } else if (format === "pdf") {
            generatePDF(dataToExport);
        }

        setExportLoading(false);
    };

    const generatePDF = (data) => {
        const doc = new jsPDF();
        
        const primaryColor = [41, 128, 185];
        const headerColor = [240, 240, 240];
        const alternateColor = [248, 249, 250];
        
        doc.setFillColor(...primaryColor);
        doc.rect(0, 0, 210, 25, 'F');
        
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        doc.text("LISTE DES CLIENTS", 105, 15, { align: 'center' });
        
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.text(`Export généré le: ${new Date().toLocaleDateString('fr-FR')}`, 15, 22);
        doc.text(`Total: ${data.length} client(s)`, 180, 22, { align: 'right' });
        
        const pageWidth = 210;
        const margin = 15;
        let yPosition = 35;
        
        const colWidths = [20, 45, 25, 25, 25, 20, 20];
        const headers = ["CODE", "RAISON SOCIALE", "TÉLÉPHONE", "EMAIL", "TOTAL ACHATS", "ENCOURS", "STATUT"];
        
        doc.setFillColor(...headerColor);
        doc.rect(margin, yPosition - 4, pageWidth - 2 * margin, 8, 'F');
        
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        
        let xPosition = margin;
        headers.forEach((header, index) => {
            doc.text(header, xPosition + 2, yPosition);
            xPosition += colWidths[index];
        });
        
        yPosition += 6;
        
        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
        
        data.forEach((item, index) => {
            if (yPosition > 270) {
                doc.addPage();
                yPosition = 20;
                
                doc.setFillColor(...headerColor);
                doc.rect(margin, yPosition - 4, pageWidth - 2 * margin, 8, 'F');
                
                doc.setTextColor(0, 0, 0);
                doc.setFontSize(8);
                doc.setFont("helvetica", "bold");
                
                let newX = margin;
                headers.forEach((header, idx) => {
                    doc.text(header, newX + 2, yPosition);
                    newX += colWidths[idx];
                });
                
                yPosition += 6;
                doc.setFontSize(7);
                doc.setFont("helvetica", "normal");
            }
            
            if (index % 2 === 0) {
                doc.setFillColor(...alternateColor);
                doc.rect(margin, yPosition - 4, pageWidth - 2 * margin, 6, 'F');
            }
            
            doc.setTextColor(0, 0, 0);
            
            const rowData = [
                item.code_client,
                item.raison_sociale.length > 20 ? item.raison_sociale.substring(0, 20) + '...' : item.raison_sociale,
                item.telephone || '-',
                item.email ? (item.email.length > 15 ? item.email.substring(0, 15) + '...' : item.email) : '-',
                formatCurrency(item.total_achats || 0),
                formatCurrency(item.encours || 0),
                getStatusBadge(item).text
            ];
            
            let currentX = margin;
            rowData.forEach((cell, cellIndex) => {
                if (cellIndex === 5) {
                    const encours = item.encours || 0;
                    if (encours > 0) doc.setTextColor(220, 53, 69);
                    else doc.setTextColor(40, 167, 69);
                } else {
                    doc.setTextColor(0, 0, 0);
                }
                
                doc.text(cell, currentX + 1, yPosition);
                currentX += colWidths[cellIndex];
            });
            
            yPosition += 6;
        });
        
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setDrawColor(200, 200, 200);
            doc.line(margin, 275, pageWidth - margin, 275);
            doc.setTextColor(100, 100, 100);
            doc.setFontSize(8);
            doc.setFont("helvetica", "normal");
            doc.text(`Page ${i} sur ${pageCount}`, 105, 282, { align: 'center' });
            doc.text("Système de Gestion", margin, 282);
            doc.text(new Date().getFullYear().toString(), pageWidth - margin, 282, { align: 'right' });
        }
        
        doc.save(`clients-${Date.now()}.pdf`);
    };

    const downloadFile = (content, type, filename) => {
        const blob = new Blob([content], { type });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
        setTimeout(() => URL.revokeObjectURL(link.href), 100);
    };

   // Dans le composant SortableHeader
const SortableHeader = ({ field, children }) => (
    <th 
        style={{ cursor: 'pointer', userSelect: 'none', position: 'relative' }}
        onClick={() => handleSort(field)}
        className="position-relative"
    >
        {children}
        {sortField === field ? (
            <i 
                className={`ri-caret-${sortDirection === 'asc' ? 'up' : 'down'}-fill ms-2`}
                style={{ fontSize: '14px', opacity: 0.8 }}
            ></i>
        ) : (
            <i 
                className="ri-arrow-down-up ms-2" 
                style={{ fontSize: '12px', opacity: 0.3 }}
            ></i>
        )}
    </th>
);

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* En-tête */}
                    <Row className="mb-4">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h4 className="mb-1 fw-bold">Gestion des clients</h4>
                                    <p className="text-muted mb-0">
                                        {stats ? `${formatNumber(stats.total_clients || 0)} clients enregistrés` : 'Liste des clients'}
                                    </p>
                                </div>
                                <div className="d-flex gap-2">
                                    <Button 
                                        variant="outline-secondary" 
                                        onClick={fetchClients}
                                        disabled={loading}
                                    >
                                        <i className={`bi ${loading ? 'bi-arrow-repeat spin' : 'bi-arrow-clockwise'} me-2`}></i>
                                        {loading ? 'Chargement...' : 'Actualiser'}
                                    </Button>
                                    
                                    <Dropdown>
                                        <Dropdown.Toggle 
                                            variant="outline-primary" 
                                            disabled={exportLoading}
                                        >
                                            <i className={`bi ${exportLoading ? 'bi-arrow-repeat spin' : 'bi-download'} me-2`}></i>
                                            {exportLoading ? 'Export...' : 'Exporter'}
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            <Dropdown.Item onClick={() => handleExport('csv')} disabled={exportLoading}>
                                                <i className="ri-file-earmark-spreadsheet me-2"></i>Export CSV
                                            </Dropdown.Item>
                                            <Dropdown.Item onClick={() => handleExport('excel')} disabled={exportLoading}>
                                                <i className="ri-file-earmark-excel me-2"></i>Export Excel
                                            </Dropdown.Item>
                                            <Dropdown.Item onClick={() => handleExport('pdf')} disabled={exportLoading}>
                                                <i className="ri-file-earmark-pdf me-2"></i>Export PDF
                                            </Dropdown.Item>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                    
                                    <Button 
                                        as={Link} 
                                        to="/ventes/clients/ajouter" 
                                        variant="primary"
                                    >
                                        <i className="ri-plus-circle me-2"></i>
                                        Nouveau client
                                    </Button>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Statistiques */}
                {stats && (
    <Row className="g-3 mb-4 stats-row">
        {/* Total clients */}
        <Col sm={6} lg={3}>
            <Card className="stat-card border-0 shadow-sm h-100">
                <Card.Body className="p-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <div className="stat-icon-wrapper bg-gradient-primary">
                            <i className="ri-group-line text-white fs-4"></i>
                        </div>
                        <span className="stat-trend-badge bg-success-light">
                            <i className="ri-arrow-up-line me-1"></i>
                            +{stats.nouveaux_mois || 0}
                        </span>
                    </div>
                    <div className="stat-content">
                        <span className="stat-label text-muted text-uppercase small fw-semibold tracking-wide">Total clients</span>
                        <h3 className="stat-value fw-bold mb-1">{formatNumber(stats.total_clients || 0)}</h3>
                        <div className="d-flex align-items-center text-muted small">
                            <i className="ri-user-add-line me-1"></i>
                            <span>{stats.nouveaux_mois || 0} nouveaux ce mois</span>
                        </div>
                        <div className="stat-progress mt-2">
                            <div className="progress" style={{ height: '4px' }}>
                                <div className="progress-bar bg-primary" style={{ width: '75%' }}></div>
                            </div>
                        </div>
                    </div>
                </Card.Body>
            </Card>
        </Col>

        {/* Clients débiteurs */}
        <Col sm={6} lg={3}>
            <Card className="stat-card border-0 shadow-sm h-100">
                <Card.Body className="p-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <div className="stat-icon-wrapper bg-gradient-danger">
                            <i className="ri-alert-line text-white fs-4"></i>
                        </div>
                        <span className="stat-trend-badge bg-danger-light">
                            <i className="ri-error-warning-line me-1"></i>
                            Attention
                        </span>
                    </div>
                    <div className="stat-content">
                        <span className="stat-label text-muted text-uppercase small fw-semibold tracking-wide">Clients débiteurs</span>
                        <h3 className="stat-value fw-bold text-danger mb-1">{formatNumber(stats.clients_debiteurs || 0)}</h3>
                        <div className="d-flex align-items-center text-muted small">
                            <i className="ri-bank-card-line me-1"></i>
                            <span>Solde moyen <strong className="text-danger">{formatCurrency(stats.solde_moyen || 0)}</strong></span>
                        </div>
                        <div className="stat-progress mt-2">
                            <div className="progress" style={{ height: '4px' }}>
                                <div className="progress-bar bg-danger" style={{ width: `${(stats.clients_debiteurs / stats.total_clients) * 100 || 0}%` }}></div>
                            </div>
                        </div>
                    </div>
                </Card.Body>
            </Card>
        </Col>

        {/* Avec crédit */}
        <Col sm={6} lg={3}>
            <Card className="stat-card border-0 shadow-sm h-100">
                <Card.Body className="p-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <div className="stat-icon-wrapper bg-gradient-success">
                            <i className="ri-bank-card-line text-white fs-4"></i>
                        </div>
                        <span className="stat-trend-badge bg-success-light">
                            <i className="ri-check-line me-1"></i>
                            Actif
                        </span>
                    </div>
                    <div className="stat-content">
                        <span className="stat-label text-muted text-uppercase small fw-semibold tracking-wide">Avec crédit</span>
                        <h3 className="stat-value fw-bold text-success mb-1">{formatNumber(stats.clients_avec_credit || 0)}</h3>
                        <div className="d-flex align-items-center text-muted small">
                            <i className="ri-secure-payment-line me-1"></i>
                            <span>Plafond disponible</span>
                        </div>
                        <div className="stat-progress mt-2">
                            <div className="progress" style={{ height: '4px' }}>
                                <div className="progress-bar bg-success" style={{ width: `${(stats.clients_avec_credit / stats.total_clients) * 100 || 0}%` }}></div>
                            </div>
                        </div>
                    </div>
                </Card.Body>
            </Card>
        </Col>

        {/* Nouveaux aujourd'hui */}
        <Col sm={6} lg={3}>
            <Card className="stat-card border-0 shadow-sm h-100">
                <Card.Body className="p-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <div className="stat-icon-wrapper bg-gradient-info">
                            <i className="ri-calendar-line text-white fs-4"></i>
                        </div>
                        <span className="stat-trend-badge bg-info-light">
                            <i className="ri-calendar-todo-line me-1"></i>
                            {stats.nouveaux_semaine || 0}/sem
                        </span>
                    </div>
                    <div className="stat-content">
                        <span className="stat-label text-muted text-uppercase small fw-semibold tracking-wide">Nouveaux</span>
                        <h3 className="stat-value fw-bold text-info mb-1">{formatNumber(stats.nouveaux_jour || 0)}</h3>
                        <div className="d-flex align-items-center text-muted small">
                            <i className="ri-time-line me-1"></i>
                            <span>Aujourd'hui</span>
                        </div>
                        <div className="stat-progress mt-2">
                            <div className="progress" style={{ height: '4px' }}>
                                <div className="progress-bar bg-info" style={{ width: `${(stats.nouveaux_jour / 10) * 100 || 0}%` }}></div>
                            </div>
                        </div>
                    </div>
                </Card.Body>
            </Card>
        </Col>
    </Row>
)}



                    {/* Erreur */}
                    {error && (
                        <Row className="mb-3">
                            <Col md={12}>
                                <Alert variant="danger" className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <i className="ri-exclamation-triangle me-2"></i>
                                        {error}
                                    </div>
                                    <div className="d-flex gap-2">
                                        <Button variant="outline-danger" size="sm" onClick={fetchClients}>
                                            Réessayer
                                        </Button>
                                        <Button variant="outline-secondary" size="sm" onClick={() => setError(null)}>
                                            Ignorer
                                        </Button>
                                    </div>
                                </Alert>
                            </Col>
                        </Row>
                    )}

                    {/* Carte principale */}
                    <Row>
                        <Col md={12}>
                            <Card className="shadow-sm border-0">
                                <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center py-3">
                                    <div className="d-flex align-items-center">
                                        <i className="ri-table me-2 fs-5"></i>
                                        <span className="fw-semibold">Liste des clients</span>
                                    </div>
                                    <div className="text-white-50 small d-flex align-items-center">
                                        {loading ? (
                                            <Spinner animation="border" size="sm" className="me-2" />
                                        ) : (
                                            <i className="ri-info-circle me-1"></i>
                                        )}
                                        <span>
                                            {loading ? 'Chargement...' : `${filteredAndSortedItems.length} client(s)`}
                                        </span>
                                        {selectedClients.length > 0 && (
                                            <span className="ms-2">
                                                • {selectedClients.length} sélectionné(s)
                                            </span>
                                        )}
                                    </div>
                                </Card.Header>
                                
                                <Card.Body className="p-0">
                                    {/* Barre d'outils */}
                                    <div className="p-3 border-bottom bg-light">
                                        <Row className="align-items-center">
                                            <Col md={6}>
                                                <InputGroup>
                                                    <InputGroup.Text className="bg-white">
                                                        <i className="ri-search text-muted"></i>
                                                    </InputGroup.Text>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="Rechercher par nom, code, email, téléphone..."
                                                        value={filterText}
                                                        onChange={e => setFilterText(e.target.value)}
                                                        className="border-start-0"
                                                    />
                                                    {filterText && (
                                                        <Button 
                                                            variant="outline-secondary" 
                                                            onClick={() => setFilterText('')}
                                                        >
                                                            <i className="ri-x"></i>
                                                        </Button>
                                                    )}
                                                </InputGroup>
                                            </Col>
                                            <Col md={6} className="text-end">
                                                <Dropdown>
                                                    <Dropdown.Toggle variant="outline-secondary" size="sm">
                                                        <i className="ri-list-ul me-2"></i>
                                                        {itemsPerPage} par page
                                                    </Dropdown.Toggle>
                                                    <Dropdown.Menu>
                                                        <Dropdown.Item onClick={() => setItemsPerPage(5)}>5 par page</Dropdown.Item>
                                                        <Dropdown.Item onClick={() => setItemsPerPage(10)}>10 par page</Dropdown.Item>
                                                        <Dropdown.Item onClick={() => setItemsPerPage(25)}>25 par page</Dropdown.Item>
                                                        <Dropdown.Item onClick={() => setItemsPerPage(50)}>50 par page</Dropdown.Item>
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            </Col>
                                        </Row>
                                    </div>

                                 
{selectedClients.length > 0 && (
    <div className="mt-2 p-2 bg-light rounded d-flex justify-content-between align-items-center">
        <span className="text-primary">
            <i className="ri-check-circle me-2"></i>
            {selectedClients.length} client(s) sélectionné(s)
        </span>
        <Button 
            variant="link" 
            size="sm" 
            className="text-danger p-0"
            onClick={() => setSelectedClients([])}
        >
            <i className="ri-x-lg"></i>
        </Button>
    </div>
)}

                                    {/* Tableau */}
                                    {/* Tableau simplifié - Sans scroll horizontal */}
{/* Tableau simplifié - Sans scroll horizontal */}
<div className="table-responsive" style={{ overflowX: 'hidden' }}>
    <Table hover className="mb-0 table-striped" style={{ minWidth: '100%', tableLayout: 'fixed' }}>
        <thead className="bg-secondary text-white">
            <tr>
                <th style={{ width: '30px' }}>
                    <div className="form-check">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            style={{
                                borderRadius: '50%',
                                width: '16px',
                                height: '16px',
                                cursor: 'pointer'
                            }}
                            checked={isAllSelected}
                            ref={input => {
                                if (input) {
                                    input.indeterminate = isSomeSelected;
                                }
                            }}
                            onChange={handleSelectAll}
                        />
                    </div>
                </th>
                <th style={{ width: '12%' }}>CODE</th>
                <th style={{ width: '25%' }}>CLIENT</th>
                <th style={{ width: '15%' }}>CONTACT</th>
                <th style={{ width: '12%' }} className="text-end">ENCOURS</th>
                <th style={{ width: '10%' }} className="text-center">STATUT</th>
                <th style={{ width: '15%' }} className="text-center">ACTIONS</th>
            </tr>
        </thead>
        <tbody>
            {loading ? (
                <tr>
                    <td colSpan="7" className="text-center py-4">
                        <div className="d-flex justify-content-center align-items-center">
                            <Spinner animation="border" variant="primary" className="me-2" />
                            <span>Chargement des clients...</span>
                        </div>
                    </td>
                </tr>
            ) : currentItems.length > 0 ? (
                currentItems.map((client) => {
                    const status = getStatusBadge(client);
                    return (
                        <tr key={client.id_client} className="align-middle">
                            <td>
                                <div className="form-check">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        style={{
                                            borderRadius: '50%',
                                            width: '16px',
                                            height: '16px',
                                            cursor: 'pointer'
                                        }}
                                        checked={selectedClients.includes(client.id_client)}
                                        onChange={() => handleSelectClient(client.id_client)}
                                    />
                                </div>
                            </td>
                            <td>
                                <Badge bg="light" text="dark" className="font-mono">
                                    {client.code_client}
                                </Badge>
                            </td>
                            <td>
                                <div className="fw-semibold text-truncate" style={{ maxWidth: '180px' }} title={client.raison_sociale}>
                                    {client.raison_sociale}
                                </div>
                                {client.numero_compte && (
                                    <small className="text-muted d-block text-truncate" style={{ maxWidth: '150px' }}>
                                        Cpt: {client.numero_compte}
                                    </small>
                                )}
                            </td>
                            <td>
                                <div className="d-flex flex-column">
                                    {client.telephone && (
                                        <small className="text-truncate" style={{ maxWidth: '120px' }}>
                                            <i className="ri-phone-line text-muted me-1"></i>
                                            {client.telephone}
                                        </small>
                                    )}
                                    {client.email && (
                                        <small className="text-truncate" style={{ maxWidth: '120px' }}>
                                            <i className="ri-mail-line text-muted me-1"></i>
                                            {client.email}
                                        </small>
                                    )}
                                    {!client.telephone && !client.email && <span className="text-muted">-</span>}
                                </div>
                            </td>
                            <td className="text-end">
                                <span className={`fw-bold ${getSoldeClass(client.encours)}`}>
                                    {formatCurrency(client.encours || 0)}
                                </span>
                                {client.encours > 0 && (
                                    <small className="d-block text-muted">
                                        {client.nb_factures || 0} facture(s)
                                    </small>
                                )}
                            </td>
                            <td className="text-center">
                                <Badge bg={status.variant} className="px-3 py-2">
                                    {status.text}
                                </Badge>
                            </td>
                            <td className="text-center">
                                <div className="d-flex justify-content-center gap-2">
                                   <Button 
    variant="outline-info" 
    size="sm"
    title="Voir détails"
    as={Link}
    to={`/ventes/clientsDetails/${client.id_client}`}
    style={{ padding: '0.25rem 0.5rem' }}
>
    <i className="ri-eye-line" style={{ fontSize: '0.9rem' }}></i>
</Button>
                                    <Button 
    variant="outline-primary" 
    size="sm"
    title="Modifier"
    as={Link}
    to={`/ventes/clients/modifier/${client.id_client}`}  // <-- Important : utiliser l'ID dynamique
    style={{ padding: '0.25rem 0.5rem' }}
>
    <i className="ri-pencil-line" style={{ fontSize: '0.9rem' }}></i>
</Button>
                                </div>
                            </td>
                        </tr>
                    );
                })
            ) : (
                <tr>
                    <td colSpan="7" className="text-center py-4">
                        <i className="ri-group-line fs-48 text-muted mb-3 d-block"></i>
                        <p className="mt-3 text-muted">Aucun client trouvé</p>
                        {filterText && (
                            <Button 
                                variant="outline-primary" 
                                size="sm"
                                onClick={() => setFilterText('')}
                            >
                                <i className="ri-refresh-line me-2"></i>
                                Réinitialiser la recherche
                            </Button>
                        )}
                    </td>
                </tr>
            )}
        </tbody>
    </Table>
</div>
                                    {/* Pagination */}
                                    {totalPages > 1 && (
                                        <div className="p-3 border-top d-flex justify-content-between align-items-center">
                                            <div className="text-muted small">
                                                Affichage de {startIndex + 1} à {Math.min(startIndex + itemsPerPage, filteredAndSortedItems.length)} sur {filteredAndSortedItems.length} clients
                                            </div>
                                            
                                            <Pagination className="mb-0">
                                                <Pagination.Prev 
                                                    onClick={() => setCurrentPage(p => Math.max(1, p-1))}
                                                    disabled={currentPage === 1}
                                                />
                                                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                                    let pageNum;
                                                    if (totalPages <= 5) {
                                                        pageNum = i + 1;
                                                    } else if (currentPage <= 3) {
                                                        pageNum = i + 1;
                                                    } else if (currentPage >= totalPages - 2) {
                                                        pageNum = totalPages - 4 + i;
                                                    } else {
                                                        pageNum = currentPage - 2 + i;
                                                    }
                                                    
                                                    return (
                                                        <Pagination.Item
                                                            key={i}
                                                            active={pageNum === currentPage}
                                                            onClick={() => setCurrentPage(pageNum)}
                                                        >
                                                            {pageNum}
                                                        </Pagination.Item>
                                                    );
                                                })}
                                                <Pagination.Next 
                                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p+1))}
                                                    disabled={currentPage === totalPages}
                                                />
                                            </Pagination>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>

                {/* Modal Détails Client */}
                <Modal 
                    show={showModal} 
                    onHide={() => setShowModal(false)}
                    size="lg"
                    centered
                    className="modal-minimal"
                >
                    <Modal.Header closeButton className="border-bottom-0 pb-0 bg-light">
                        <Modal.Title className="w-100">
                            <div className="d-flex align-items-center mb-2">
                                <div className="icon-container bg-success-soft p-2 rounded-circle me-3">
                                    <i className="ri-eye-line text-white fs-4"></i>
                                </div>
                                <div>
                                    <h5 className="mb-0 fw-semibold text-white">Détails du client</h5>
                                    <small className="text-light">Informations complètes</small>
                                </div>
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    
                    <Modal.Body className="pt-2" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                        {selectedClient && (
                            <div className="client-details-minimal">
                                {/* En-tête statut */}
                                <div className="status-card p-3 mb-3 rounded-3 bg-light-subtle border">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div className="d-flex align-items-center">
                                            <div className={`status-indicator me-3 ${
                                                selectedClient.encours > 0 ? 'bg-warning' :
                                                selectedClient.total_achats > 0 ? 'bg-success' : 'bg-secondary'
                                            }`}></div>
                                            <div>
                                                <h5 className="mb-1 fw-semibold">{selectedClient.raison_sociale}</h5>
                                                <p className="text-muted small mb-0">
                                                    Code: {selectedClient.code_client} • Inscrit le {formatDate(selectedClient.date_creation)}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="text-end">
                                            <span className={`badge rounded-pill ${
                                                selectedClient.encours > 0 ? 'bg-warning' :
                                                selectedClient.total_achats > 0 ? 'bg-success' : 'bg-secondary'
                                            }`}>
                                                {selectedClient.encours > 0 ? 'En cours' :
                                                 selectedClient.total_achats > 0 ? 'Actif' : 'Inactif'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Informations de contact */}
                                <div className="message-content-card mb-4">
                                    <div className="d-flex align-items-center mb-2">
                                        <i className="ri-info-circle text-primary me-2"></i>
                                        <h6 className="mb-0 text-dark fw-semibold">Coordonnées</h6>
                                    </div>
                                    
                                    <Row className="g-2 mb-3">
                                        <Col md={6}>
                                            <div className="info-item p-2 rounded-2 border">
                                                <div className="d-flex align-items-center">
                                                    <i className="ri-phone text-muted me-2"></i>
                                                    <div>
                                                        <div className="text-muted small">Téléphone</div>
                                                        <span className="fw-semibold">{selectedClient.telephone || 'Non renseigné'}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="info-item p-2 rounded-2 border">
                                                <div className="d-flex align-items-center">
                                                    <i className="ri-envelope text-muted me-2"></i>
                                                    <div>
                                                        <div className="text-muted small">Email</div>
                                                        <span className="fw-semibold">{selectedClient.email || 'Non renseigné'}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col md={12}>
                                            <div className="info-item p-2 rounded-2 border">
                                                <div className="d-flex align-items-center">
                                                    <i className="ri-geo-alt text-muted me-2"></i>
                                                    <div>
                                                        <div className="text-muted small">Adresse</div>
                                                        <span className="fw-semibold">{selectedClient.adresse || 'Non renseignée'}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>

                                {/* Identifiants juridiques */}
                                <div className="technical-section mb-4">
                                    <div className="d-flex align-items-center mb-3">
                                        <i className="ri-briefcase text-muted me-2"></i>
                                        <h6 className="mb-0 text-dark fw-semibold">Identifiants juridiques</h6>
                                    </div>
                                    
                                    <Row className="g-2">
                                        {[
                                            { icon: 'bi-credit-card', label: 'Numéro compte', value: selectedClient.numero_compte },
                                            { icon: 'bi-building', label: 'ICE', value: selectedClient.ice },
                                            { icon: 'bi-file-text', label: 'Patente', value: selectedClient.patente },
                                            { icon: 'bi-journal', label: 'RC', value: selectedClient.rc },
                                            { icon: 'bi-calculator', label: 'IF', value: selectedClient.identifiant_fiscal },
                                            { icon: 'bi-shield', label: 'CNSS', value: selectedClient.cnss }
                                        ].map((item, index) => (
                                            <Col md={6} key={index}>
                                                <div className="tech-item p-2 rounded-2 border mb-2">
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <div className="d-flex align-items-center">
                                                            <i className={`${item.icon} text-muted me-2`}></i>
                                                            <span className="text-muted small">{item.label}</span>
                                                        </div>
                                                        <span className="fw-semibold text-dark">{item.value || '-'}</span>
                                                    </div>
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>
                                </div>

                                {/* Informations financières */}
                                <div className="technical-section mb-4">
                                    <div className="d-flex align-items-center mb-3">
                                        <i className="ri-currency-exchange text-muted me-2"></i>
                                        <h6 className="mb-0 text-dark fw-semibold">Informations financières</h6>
                                    </div>
                                    
                                    <Row className="g-2">
                                        <Col md={4}>
                                            <div className="p-3 rounded-3 border text-center">
                                                <div className="text-muted small mb-1">Total achats</div>
                                                <h5 className="mb-0 text-primary" >{formatCurrency(selectedClient.total_achats || 0)}</h5>
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="p-3 rounded-3 border text-center">
                                                <div className="text-muted small mb-1">Encours</div>
                                                <h5 className={`mb-0 ${getSoldeClass(selectedClient.encours)}`}>
                                                    {formatCurrency(selectedClient.encours || 0)}
                                                </h5>
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="p-3 rounded-3 border text-center">
                                                <div className="text-muted small mb-1">Plafond crédit</div>
                                                <h5 className="mb-0 text-info">{formatCurrency(selectedClient.plafond_credit || 0)}</h5>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>

                                {/* Dernières factures */}
                                {selectedClient.factures?.length > 0 && (
                                    <div className="technical-section mb-4">
                                        <div className="d-flex align-items-center mb-3">
                                            <i className="ri-receipt text-muted me-2"></i>
                                            <h6 className="mb-0 text-dark fw-semibold">Dernières factures</h6>
                                        </div>
                                        
                                        <div className="table-responsive">
                                            <Table size="sm" className="mb-0">
                                                <thead className="bg-light">
                                                    <tr>
                                                        <th>Facture</th>
                                                        <th>Date</th>
                                                        <th className="text-end">Montant</th>
                                                        <th className="text-end">Reste</th>
                                                        <th>Statut</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {selectedClient.factures.map(facture => (
                                                        <tr key={facture.id_facture}>
                                                            <td>{facture.numero_facture}</td>
                                                            <td>{formatDate(facture.date_facture)}</td>
                                                            <td className="text-end">{formatCurrency(facture.montant)}</td>
                                                            <td className="text-end text-danger">{formatCurrency(facture.reste)}</td>
                                                            <td>
                                                                <Badge bg={
                                                                    facture.statut === 'payee' ? 'success' :
                                                                    facture.statut === 'payee_partielle' ? 'warning' : 'danger'
                                                                }>
                                                                    {facture.statut}
                                                                </Badge>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </div>
                                )}

                                {/* Métadonnées */}
                                <div className="metadata-section mt-4 pt-3 border-top">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="text-muted small">
                                            ID: #{selectedClient.id_client} • Créé le {formatDate(selectedClient.date_creation)}
                                            {selectedClient.date_modification && ` • Modifié le ${formatDate(selectedClient.date_modification)}`}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </Modal.Body>
                    
                    <Modal.Footer className="border-top-0 pt-0">
                        <Button 
                            variant="outline-secondary" 
                            onClick={() => setShowModal(false)}
                            className="px-4"
                        >
                            Fermer
                        </Button>
                        <Button 
                            variant="primary" 
                            className="px-4"
                            as={Link}
                            to={`/ventes/clients/modifier/${selectedClient?.id_client}`}
                        >
                            <i className="ri-pencil me-2"></i>
                            Modifier
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Styles CSS intégrés */}
                <style>{`
                    .modal-minimal .modal-content {
                        border: none;
                        border-radius: 12px;
                        box-shadow: 0 10px 40px rgba(0,0,0,0.1);
                    }

                    .modal-minimal .modal-header .btn-close {
                        padding: 0.5rem;
                        margin: -0.5rem -0.5rem -0.5rem auto;
                    }

                    .status-indicator {
                        width: 8px;
                        height: 40px;
                        border-radius: 4px;
                    }

                    .icon-container {
                        width: 44px;
                        height: 44px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }

                    .bg-primary-soft {
                        background-color: rgba(13, 110, 253, 0.1);
                    }

                    .bg-success-subtle {
                        background-color: rgba(25, 135, 84, 0.1);
                    }

                    .bg-info-subtle {
                        background-color: rgba(13, 202, 240, 0.1);
                    }

                    .bg-warning-subtle {
                        background-color: rgba(255, 193, 7, 0.1);
                    }

                    .bg-danger-subtle {
                        background-color: rgba(220, 53, 69, 0.1);
                    }

                    .tech-item {
                        transition: all 0.2s ease;
                        background: #fafafa;
                    }

                    .tech-item:hover {
                        background: #f8f9fa;
                        transform: translateY(-1px);
                    }

                    .info-item {
                        transition: all 0.2s ease;
                        background: #fff;
                    }

                    .info-item:hover {
                        background: #f8f9fa;
                    }

                    .font-mono {
                        font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', monospace;
                    }

                    .spin {
                        animation: spin 1s linear infinite;
                    }

                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }

                    .table-striped > tbody > tr:nth-of-type(odd) {
                        background-color: rgba(0,0,0,.02);
                    }

                    .table-hover > tbody > tr:hover {
                        background-color: rgba(13, 110, 253, 0.05);
                    }

                    .badge {
                        padding: 0.5em 0.75em;
                        font-weight: 500;
                    }




                    .stats-row .stat-card {
    border-radius: 20px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
    position: relative;
    overflow: hidden;
}

.stats-row .stat-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, transparent, rgba(67, 97, 238, 0.2), transparent);
    transform: translateX(-100%);
    transition: transform 0.6s ease;
}

.stats-row .stat-card:hover::before {
    transform: translateX(100%);
}

.stats-row .stat-card:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04) !important;
}

.stat-icon-wrapper {
    width: 52px;
    height: 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 16px;
    transition: all 0.3s ease;
    box-shadow: 0 4px 10px rgba(0,0,0,0.1);
}

.stat-card:hover .stat-icon-wrapper {
    transform: scale(1.1) rotate(5deg);
}

.bg-gradient-primary {
    background: linear-gradient(135deg, #4361ee, #3a56d4);
}

.bg-gradient-danger {
    background: linear-gradient(135deg, #ef4444, #dc2626);
}

.bg-gradient-success {
    background: linear-gradient(135deg, #10b981, #059669);
}

.bg-gradient-info {
    background: linear-gradient(135deg, #3b82f6, #2563eb);
}

.stat-trend-badge {
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    backdrop-filter: blur(4px);
}

.bg-success-light {
    background: rgba(16, 185, 129, 0.15);
    color: #059669;
}

.bg-danger-light {
    background: rgba(239, 68, 68, 0.15);
    color: #dc2626;
}

.bg-info-light {
    background: rgba(59, 130, 246, 0.15);
    color: #2563eb;
}

.stat-label {
    letter-spacing: 0.5px;
}

.stat-value {
    font-size: 2rem;
    line-height: 1.2;
}

.stat-progress {
    opacity: 0.6;
    transition: opacity 0.3s ease;
}

.stat-card:hover .stat-progress {
    opacity: 1;
}

.progress {
    background-color: #e9ecef;
    border-radius: 10px;
    overflow: hidden;
}

.progress-bar {
    border-radius: 10px;
    transition: width 0.6s ease;
}

.tracking-wide {
    letter-spacing: 0.5px;
}

/* Animation d'entrée */
.stats-row .col {
    animation: slideInUp 0.5s ease forwards;
    opacity: 0;
}

.stats-row .col:nth-child(1) { animation-delay: 0.1s; }
.stats-row .col:nth-child(2) { animation-delay: 0.2s; }
.stats-row .col:nth-child(3) { animation-delay: 0.3s; }
.stats-row .col:nth-child(4) { animation-delay: 0.4s; }

@keyframes slideInUp {
    from {
        opacity: 0;
        transform: translateY(30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* Version responsive */
@media (max-width: 768px) {
    .stat-value {
        font-size: 1.5rem;
    }
    
    .stat-icon-wrapper {
        width: 44px;
        height: 44px;
    }
    
    .stat-icon-wrapper i {
        font-size: 1.2rem;
    }
}
                `}</style>

                <Footer />
            </div>
        </React.Fragment>
    );
}